import {useMutation} from '@tanstack/react-query';
import {apiClient} from '../../../http/query-client';
import {showHttpErrorToast} from '../../../utils/http/show-http-error-toast';
import {onFormQueryError} from '../../../errors/on-form-query-error';
import {Dialog} from '../../../ui/overlays/dialog/dialog';
import {DialogHeader} from '../../../ui/overlays/dialog/dialog-header';
import {DialogBody} from '../../../ui/overlays/dialog/dialog-body';
import {DialogFooter} from '../../../ui/overlays/dialog/dialog-footer';
import {Button} from '../../../ui/buttons/button';
import {Form} from '../../../ui/forms/form';
import {useForm} from 'react-hook-form';
import {FormTextField} from '../../../ui/forms/input-field/text-field/text-field';
import {Trans} from '../../../i18n/trans';
import {useDialogContext} from '../../../ui/overlays/dialog/dialog-context';

interface Props {
  onConfirm: () => void;
}

export function ConfirmPasswordDialog({onConfirm}: Props) {
  const {close} = useDialogContext();
  const form = useForm<{password: string}>({defaultValues: {password: ''}});

  const confirmMutation = useMutation((password: string) => apiClient.post('auth/user/confirm-password', {password}), {
    onSuccess: () => {
      close();
      onConfirm();
    },
    onError: err => {
      onFormQueryError(err, form);
    }
  });

  return (
    <Dialog size="sm">
      <DialogHeader>
        <Trans message="Confirm Password" />
      </DialogHeader>
      <DialogBody>
        <div className="mb-24">
          <Trans message="For your security, please confirm your password to continue." />
        </div>
        <Form
          form={form}
          id="confirm-password-form"
          onSubmit={data => confirmMutation.mutate(data.password)}
        >
          <FormTextField
            name="password"
            type="password"
            label={<Trans message="Password" />}
            required
            autoFocus
          />
        </Form>
      </DialogBody>
      <DialogFooter>
        <Button variant="text" onClick={() => close()}>
          <Trans message="Cancel" />
        </Button>
        <Button
          type="submit"
          form="confirm-password-form"
          variant="flat"
          color="primary"
          disabled={confirmMutation.isLoading}
        >
          <Trans message="Confirm" />
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
